import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { api } from '../api/client'
import VoiceInputField from '../components/VoiceInputField'

function num(v) {
  if (v === null || v === undefined || v === '') return null
  const n = Number(v)
  return Number.isNaN(n) ? null : n
}

export default function DailyRecordPage() {
  const { date } = useParams()
  const navigate = useNavigate()
  const [form, setForm] = useState({})
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    api(`/records/${date}`)
      .then((data) => {
        setForm({
          blood_pressure_sys: data.blood_pressure_sys ?? '',
          blood_pressure_dia: data.blood_pressure_dia ?? '',
          heart_rate: data.heart_rate ?? '',
          body_temperature: data.body_temperature ?? '',
          weight: data.weight ?? '',
          ultrafiltration_1: data.ultrafiltration_1 ?? '',
          ultrafiltration_2: data.ultrafiltration_2 ?? '',
          ultrafiltration_3: data.ultrafiltration_3 ?? '',
          ultrafiltration_4: data.ultrafiltration_4 ?? '',
          ultrafiltration_5: data.ultrafiltration_5 ?? '',
          morning_blood_sugar: data.morning_blood_sugar ?? '',
          bedtime_blood_sugar: data.bedtime_blood_sugar ?? '',
          insulin_rapid: data.insulin_rapid ?? '',
          insulin_long: data.insulin_long ?? '',
        })
      })
      .catch(() => setForm({}))
      .finally(() => setLoading(false))
  }, [date])

  const setField = (key) => (v) => {
    setForm((f) => ({ ...f, [key]: String(v) }))
    setSaved(false)
  }

  const totalUf = useMemo(
    () =>
      [1, 2, 3, 4, 5].reduce(
        (sum, i) => sum + (num(form[`ultrafiltration_${i}`]) ?? 0),
        0
      ),
    [form]
  )

  const save = async () => {
    setError('')
    setSaved(false)
    const payload = {
      date,
      blood_pressure_sys: num(form.blood_pressure_sys),
      blood_pressure_dia: num(form.blood_pressure_dia),
      heart_rate: num(form.heart_rate),
      body_temperature: num(form.body_temperature),
      weight: num(form.weight),
      ultrafiltration_1: num(form.ultrafiltration_1),
      ultrafiltration_2: num(form.ultrafiltration_2),
      ultrafiltration_3: num(form.ultrafiltration_3),
      ultrafiltration_4: num(form.ultrafiltration_4),
      ultrafiltration_5: num(form.ultrafiltration_5),
      morning_blood_sugar: num(form.morning_blood_sugar),
      bedtime_blood_sugar: num(form.bedtime_blood_sugar),
      insulin_rapid: num(form.insulin_rapid),
      insulin_long: num(form.insulin_long),
    }
    try {
      await api('/records', { method: 'POST', body: payload })
      setSaved(true)
    } catch (e) {
      setError(e.message)
    }
  }

  if (loading) return <p className="text-center text-2xl text-gray-300">載入中…</p>

  return (
    <div>
      <div className="mb-3 flex items-center justify-between">
        <button
          type="button"
          onClick={() => navigate('/records')}
          className="h-14 w-20 rounded-2xl bg-navy-700 text-2xl font-bold text-white"
        >
          ‹ 回
        </button>
        <h1 className="text-3xl font-bold text-white">{date} 記錄</h1>
        <div className="w-20" />
      </div>

      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <VoiceInputField label="收縮壓" unit="毫米汞柱" type="number" value={form.blood_pressure_sys} onChange={setField('blood_pressure_sys')} hint="例：血壓一百二" />
          <VoiceInputField label="舒張壓" unit="毫米汞柱" type="number" value={form.blood_pressure_dia} onChange={setField('blood_pressure_dia')} hint="例：下壓八十" />
        </div>

        <VoiceInputField label="心跳" unit="下/分" type="number" value={form.heart_rate} onChange={setField('heart_rate')} hint="例：心跳七十二" />
        <VoiceInputField label="體溫" unit="°C" type="decimal" value={form.body_temperature} onChange={setField('body_temperature')} hint="例：三十六點五" />
        <VoiceInputField label="體重" unit="公斤" type="decimal" value={form.weight} onChange={setField('weight')} hint="例：體重六十五點三" />

        <div className="rounded-2xl bg-navy-700 p-3">
          <h2 className="mb-2 text-2xl font-bold text-white">💧 今天洗水（超濾量）</h2>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <VoiceInputField
                key={i}
                label={`第 ${i} 次`}
                unit="毫升"
                type="number"
                value={form[`ultrafiltration_${i}`]}
                onChange={setField(`ultrafiltration_${i}`)}
                hint="例：洗水五百"
              />
            ))}
          </div>
          <p className="mt-2 rounded-xl bg-white p-3 text-center text-3xl font-bold text-navy-900">
            總共洗水 <span className="text-warn">{totalUf}</span> 毫升
          </p>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <VoiceInputField label="睡前血糖" unit="mg/dL" type="number" value={form.bedtime_blood_sugar} onChange={setField('bedtime_blood_sugar')} hint="例：睏前血糖一二五（昨天睡前）" />
          <VoiceInputField label="起床血糖" unit="mg/dL" type="number" value={form.morning_blood_sugar} onChange={setField('morning_blood_sugar')} hint="例：起床血糖一一零（今天起床）" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <VoiceInputField label="速效胰島素" unit="單位" type="decimal" value={form.insulin_rapid} onChange={setField('insulin_rapid')} hint="例：速效四" />
          <VoiceInputField label="長效胰島素" unit="單位" type="decimal" value={form.insulin_long} onChange={setField('insulin_long')} hint="例：長效十二" />
        </div>
      </div>

      {error && (
        <p className="mt-3 rounded-xl bg-red-100 p-2 text-center text-xl font-bold text-warn">⚠️ {error}</p>
      )}
      {saved && (
        <p className="mt-3 rounded-xl bg-green-100 p-2 text-center text-xl font-bold text-safe">✅ 已經存好了！</p>
      )}

      <div className="mt-4 space-y-3">
        <button type="button" onClick={save} className="big-btn bg-safe text-white hover:bg-green-600">
          💾 存起來
        </button>
        <button
          type="button"
          onClick={() => navigate('/records')}
          className="big-btn bg-navy-700 text-white"
        >
          📋 回記錄列表
        </button>
      </div>
    </div>
  )
}
